// ---------------------------------------------------------------------------
// footer-nav.tsx — Sticky bottom navigation bar.
// Back / Cancel on left, Next (or Create on step 4) on right.
// Cancel navigates back to /studio (dashboard).
// ---------------------------------------------------------------------------

import React from 'react'
import styles from './new-project-wizard-page.module.css'
import { useStudioLocale } from '@/i18n'
import { useWizard, canAdvance } from './wizard-state'

interface FooterNavProps {
  onCancel: () => void
  onCreate: () => void
  /** Both are REQUIRED, not defaulted. A default would be an English literal
   *  in a component that has no locale of its own to translate it with, and
   *  the parent already knows which of the two labels applies. */
  createLabel: string
  creatingLabel: string
}

export function FooterNav({
  onCancel,
  onCreate,
  createLabel,
  creatingLabel,
}: FooterNavProps) {
  const copy = useStudioLocale().strings.newProject.footer
  const { state, dispatch } = useWizard()
  const { step, isCreating } = state
  const isLastStep = step === 4
  const advanceOk = canAdvance(state)

  return (
    <footer className={styles.footerNav}>
      <div className={styles.footerLeft}>
        {step > 1 ? (
          <button
            type="button"
            className={styles.btnGhost}
            onClick={() => dispatch({ type: 'BACK' })}
            disabled={isCreating}
            data-testid="new-project-back"
          >
            {copy.back}
          </button>
        ) : null}
        <button
          type="button"
          className={styles.btnCancel}
          onClick={onCancel}
          disabled={isCreating}
          data-testid="new-project-cancel"
        >
          {copy.cancel}
        </button>
      </div>

      <div className={styles.footerRight}>
        {!isLastStep ? (
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={() => dispatch({ type: 'NEXT' })}
            disabled={!advanceOk}
            data-testid="new-project-next"
          >
            {copy.next}
          </button>
        ) : (
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={onCreate}
            disabled={isCreating}
            data-testid="new-project-create"
          >
            {isCreating ? creatingLabel : createLabel}
          </button>
        )}
      </div>
    </footer>
  )
}
